import { estimateOnsetFromFrame } from './Clock';

/* ────────────────────────────────────────────────────────────
   단순반응시간 프로브
   ──────────────────────────────────────────────────────────── */

export interface ProbeTrial {
  /** 자극 온셋 → 입력 이벤트 생성 시각. event.timeStamp 기준. */
  rtMs: number;
  /**
   * 이벤트 생성 → 핸들러 실행까지의 지연.
   * 순수하게 기계적인 값이다(메인 스레드 큐잉). 사람의 반응은 섞이지 않는다.
   * 핸들러 안에서 performance.now() 를 쓰면 이 지연이 RT 에 통째로 더해진다 —
   * 우리가 event.timeStamp 를 쓰는 이유가 바로 이 숫자다.
   */
  handlerDelayMs: number;
  source: 'keyboard' | 'pointer';
}

export interface InputProbeResult {
  trials: ProbeTrial[];
  medianRtMs: number;
  /**
   * 이 SD 가 이번 측정의 핵심이다.
   * 평균 RT 에는 사람의 개인차와 기기의 상수 지연이 섞여 있어 해석이 어렵지만,
   * SD 는 "이 기기에서 RT 차이를 얼마나 잘 잴 수 있는가"의 상한을 알려준다.
   */
  sdRtMs: number;
  meanHandlerDelayMs: number;
  sdHandlerDelayMs: number;
  /** RT < 100 ms — 자극을 보고 반응한 것이 아니라 미리 누른 것. */
  anticipations: number;
  timeouts: number;
}

export interface ProbeOptions {
  trials?: number;
  frameMs: number;
  itiRangeMs?: [number, number];
  timeoutMs?: number;
  /** 자극을 그린다/지운다. 화면 쪽이 시각 표현을 소유한다. */
  render: (visible: boolean) => void;
  onProgress?: (done: number, total: number) => void;
  signal?: AbortSignal;
}

/**
 * 12 트라이얼짜리 단순반응 과제. 화면에 사각형이 뜨면 최대한 빨리 누른다.
 *
 * 사람의 RT 와 기기의 지연을 분리할 수는 없다(그러려면 광센서와 로봇 손가락이 필요하다).
 * 우리가 얻는 것은 두 가지다 — 이 참가자·이 기기 조합의 RT 변산 기저선, 그리고
 * 순수 기계 지표인 핸들러 지연.
 */
export async function runInputProbe(opts: ProbeOptions): Promise<InputProbeResult> {
  const total = opts.trials ?? 12;
  const [itiMin, itiMax] = opts.itiRangeMs ?? [900, 2200];
  const timeoutMs = opts.timeoutMs ?? 2000;
  const trials: ProbeTrial[] = [];
  let anticipations = 0;
  let timeouts = 0;

  for (let i = 0; i < total; i += 1) {
    if (opts.signal?.aborted) break;
    opts.render(false);
    await delay(itiMin + Math.random() * (itiMax - itiMin), opts.signal);
    if (opts.signal?.aborted) break;

    const outcome = await oneTrial(opts.render, opts.frameMs, timeoutMs, opts.signal);
    opts.render(false);

    if (outcome === 'timeout') {
      timeouts += 1;
    } else {
      if (outcome.rtMs < 100) anticipations += 1;
      else trials.push(outcome);
    }
    opts.onProgress?.(i + 1, total);
  }

  const rts = trials.map((t) => t.rtMs).sort((a, b) => a - b);
  const hds = trials.map((t) => t.handlerDelayMs);
  return {
    trials,
    medianRtMs: median(rts),
    sdRtMs: sd(rts),
    meanHandlerDelayMs: mean(hds),
    sdHandlerDelayMs: sd(hds),
    anticipations,
    timeouts,
  };
}

function oneTrial(
  render: (v: boolean) => void,
  frameMs: number,
  timeoutMs: number,
  signal: AbortSignal | undefined,
): Promise<ProbeTrial | 'timeout'> {
  return new Promise((resolve) => {
    let onset = 0;
    let done = false;

    const finish = (v: ProbeTrial | 'timeout'): void => {
      if (done) return;
      done = true;
      window.removeEventListener('keydown', onKey, true);
      window.removeEventListener('pointerdown', onPointer, true);
      window.clearTimeout(timer);
      resolve(v);
    };

    const onKey = (e: KeyboardEvent): void => {
      if (e.repeat || onset === 0) return;
      finish({
        rtMs: e.timeStamp - onset,
        handlerDelayMs: performance.now() - e.timeStamp,
        source: 'keyboard',
      });
    };
    const onPointer = (e: PointerEvent): void => {
      if (onset === 0) return;
      finish({
        rtMs: e.timeStamp - onset,
        handlerDelayMs: performance.now() - e.timeStamp,
        source: 'pointer',
      });
    };

    // capture:true + passive:true — 다른 핸들러보다 먼저, 그리고 preventDefault 없이.
    window.addEventListener('keydown', onKey, true);
    window.addEventListener('pointerdown', onPointer, { capture: true, passive: true });
    signal?.addEventListener('abort', () => finish('timeout'), { once: true });

    let timer = 0;
    requestAnimationFrame((ts) => {
      render(true);
      // 이 콜백에서 그린 것은 다음 vsync 에 표시된다 → 온셋은 ts + 한 프레임.
      onset = estimateOnsetFromFrame(ts, frameMs);
      timer = window.setTimeout(() => finish('timeout'), timeoutMs);
    });
  });
}

function delay(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    const t = window.setTimeout(resolve, ms);
    signal?.addEventListener('abort', () => { window.clearTimeout(t); resolve(); }, { once: true });
  });
}

/* ────────────────────────────────────────────────────────────
   물리 크기
   ──────────────────────────────────────────────────────────── */

/** ISO/IEC 7810 ID-1 (신용카드) 가로 길이. */
export const ID1_CARD_WIDTH_MM = 85.6;

/** 화면의 카드 그림을 실제 카드에 맞췄을 때의 CSS px 폭 → mm 당 px. */
export function pxPerMmFromCard(cardWidthCssPx: number): number {
  return cardWidthCssPx / ID1_CARD_WIDTH_MM;
}

/**
 * 시각도(visual angle). 자극의 물리 크기와 시청 거리가 있어야 세션 간·기기 간 비교가 성립한다.
 * 점 탐침에서 자극쌍의 이심률(eccentricity)은 주의 이동 거리를 결정하므로 심리학적으로 유의미하다.
 */
export function visualAngleDeg(sizeMm: number, viewingDistanceMm: number): number {
  return (2 * Math.atan(sizeMm / (2 * viewingDistanceMm)) * 180) / Math.PI;
}

export function mmForVisualAngle(deg: number, viewingDistanceMm: number): number {
  return 2 * viewingDistanceMm * Math.tan((deg * Math.PI) / 360);
}

/* ────────────────────────────────────────────────────────────
   작은 통계 도우미
   ──────────────────────────────────────────────────────────── */

function mean(xs: number[]): number {
  return xs.length === 0 ? 0 : xs.reduce((a, b) => a + b, 0) / xs.length;
}
function sd(xs: number[]): number {
  if (xs.length < 2) return 0;
  const m = mean(xs);
  return Math.sqrt(xs.reduce((a, b) => a + (b - m) ** 2, 0) / (xs.length - 1));
}
function median(sortedAsc: number[]): number {
  const n = sortedAsc.length;
  if (n === 0) return 0;
  const mid = Math.floor(n / 2);
  return n % 2 ? (sortedAsc[mid] ?? 0) : ((sortedAsc[mid - 1] ?? 0) + (sortedAsc[mid] ?? 0)) / 2;
}
